import { Fragment, useEffect, useMemo, useState } from 'react';
import { Box, List, ListItem, ListItemAvatar, ListItemButton, ListItemText, Stack, Typography } from '@mui/material';
import { UserAvatar3 } from 'sections/chat/UserAvatar';
import { dateHandler } from 'pages/globals/global';

import InfiniteScroll from 'react-infinite-scroll-component';
import CircularWithPath from 'components/@extended/progress/CircularWithPath';

import EmptyUI from 'components/@extended/Epmty';

import { useGetMessageNotificationMutation, useMarkReadMessageNotificationMutation } from 'store/features/profile/profileApi';
import { useSelector } from 'react-redux';

const LIST_SX = {
	width: '100%',
	maxWidth: '100%',
	height: 430,
	maxHeight: 430,
	backgroundColor: '#FFFFFF',
	overflow: 'auto',
	overscrollBehavior: 'contain',
	'&::-webkit-scrollbar': {
		width: '6px',
	},
	'&::-webkit-scrollbar-thumb': {
		backgroundColor: '#aeb0b0',
		borderRadius: '6px',
	},
	p: '10px 20px 12px',
	'& .MuiListItemButton-root': {
		px: 1.5,
		py: 1.2,
		borderRadius: '14px',
		backgroundColor: '#F9FAFB',
		alignItems: 'flex-start',
		mb: '12px',
		'&:hover': { bgcolor: '#F3F4F6' },
		'& .MuiListItemAvatar-root': {
			minWidth: 60,
			mr: 0,
			alignSelf: 'flex-start',
			mt: '2px',
		},
		'& .MuiAvatar-root': {
			mt: '2px',
			ml: 0,
			top: 'auto',
			right: 'auto',
			alignSelf: 'flex-start',
			transform: 'none',
			borderRadius: '50%',
			overflow: 'hidden',
		},
	},
};

const getItemSx = (isUnread) => ({
	bgcolor: isUnread ? '#E8F3FF' : '#F9FAFB',
	'&:hover': { bgcolor: isUnread ? '#DDEEFF' : '#F3F4F6' },
});

const renderMessageText = (value) => (
	<>
		<Typography sx={{ color: '#334155', fontSize: '14px', lineHeight: 1.45 }}>
			<Typography component="span" sx={{ color: '#1E293B', fontWeight: 500, fontSize: '14px' }}>
				{value?.userFrom?.entityFullName}
			</Typography>{' '}
			has sent a message in case no{' '}
			<Typography component="span" sx={{ color: '#2563EB', fontWeight: 500, fontSize: '14px' }}>
				{value?.lynUniqueId}
			</Typography>
		</Typography>
		<Typography sx={{ color: '#64748B', fontSize: '12px', mt: 0.2 }}>{dateHandler(value?.createdDate)}</Typography>
	</>
);

function MessageContent({ searchString, clickHandler }) {
	const [GetMessageNotification, { data: messageData, isSuccess, isLoading }] = useGetMessageNotificationMutation();
	const [MarkReadMessageNotification] = useMarkReadMessageNotificationMutation();
	const userId = useSelector((state) => state?.profile?.profileData?.userId);

	const size = 10;
	const [pageNumber, setPageNumber] = useState(0);
	const [hasMore, setHasMore] = useState(false);
	const [completedMessList, setCompletedMessList] = useState([]);
	const [newMessageList, setNewMessageList] = useState([]);

	const search = useMemo(() => {
		return [...newMessageList, ...completedMessList]?.filter((item) => {
			const content = `${item?.userFrom?.entityFullName} ${item?.lynUniqueId}`;

			return content.toLocaleLowerCase().includes(searchString.toLocaleLowerCase());
		});
		//eslint-disable-next-line
	}, [searchString]);

	const isDateOlder = (dateString) => {
		// Convert the given date string to a Date object
		const givenDate = new Date(dateString);

		// Get the current date and time
		const currentDate = new Date();

		// Subtract 15 days in milliseconds
		const sevenDaysAgo = new Date(currentDate.getTime() - 15 * 24 * 60 * 60 * 1000);

		// Compare the given date with 7 days ago
		return givenDate < sevenDaysAgo;
	};

	const isUnreadMessage = (message) => {
		if (!userId) return false;
		const currentUser = message?.messageTos?.find((item) => item?.userId === userId);
		return currentUser?.clicked !== 'clicked';
	};

	useEffect(() => {
		if (pageNumber > -1 && size) {
			GetMessageNotification({
				page: pageNumber,
				size,
			});
		}
	}, [pageNumber]);

	useEffect(() => {
		if (isSuccess) {
			const oldList = messageData?.allMessages?.length ?? 0;
			const newList = messageData?.unreadMessages?.length ?? 0;
			const total = messageData?.totalCount ?? 0;

			const currentCompletedNotList = completedMessList.length + oldList + newList + newMessageList.length;

			if (currentCompletedNotList >= total) {
				setHasMore(false);
			} else {
				setHasMore(true);
			}

			if (oldList > 0) {
				setCompletedMessList((prev) => [...prev, ...messageData.allMessages]);
			}

			if (newList > 0) {
				setNewMessageList(messageData.unreadMessages);

				// let unreadMessageIdList = newMessageList.map((item) => item.id);

				MarkReadMessageNotification({
					ids: [],
				});
			}
		}
	}, [isSuccess]);

	if (!hasMore && !isSuccess && isLoading) {
		return (
			<ListItem>
				<Stack width="100%" alignItems="center" justifyContent="center">
					<CircularWithPath size="medium" />
				</Stack>
			</ListItem>
		);
	}

	if (
		(isSuccess && !isLoading && messageData?.allMessages?.length === 0 && messageData?.unreadMessages?.length === 0) ||
		(searchString !== '' && search?.length === 0)
	) {
		return <EmptyUI name={'Notification'} />;
	}

	if (!isLoading && searchString !== '') {
		return <MessageSearchContent search={search} clickHandler={clickHandler} isUnreadMessage={isUnreadMessage} />;
	}

	return (
		<List component="nav" sx={LIST_SX}>
			<InfiniteScroll
				dataLength={completedMessList.length}
				next={() => {
					setPageNumber((prev) => prev + 1);
				}}
				hasMore={hasMore}
				loader={
					<ListItem>
						<Stack width="100%" alignItems="center" justifyContent="center">
							<CircularWithPath size="medium" />
						</Stack>
					</ListItem>
				}
				scrollableTarget="completed-notification-list"
			>
				{/* New Section */}
				{newMessageList?.length > 0 && searchString === '' && (
					<Box>
						{[...newMessageList]
							?.sort((a, b) => b?.createdDate?.timeLong - a?.createdDate?.timeLong)
							.map((value, index) => (
								<Fragment key={index}>
									{index === 0 && (
										<Box sx={{ px: 0, pt: 0, pb: '12px' }}>
											<Typography sx={{ color: '#1E293B', fontSize: '1.04rem', fontWeight: 500 }}>New</Typography>
										</Box>
									)}
									<ListItemButton key={index} sx={getItemSx(true)} onClick={() => clickHandler(value)}>
										<ListItemAvatar>
											<UserAvatar3 userId={value?.userId} width={48} height={48} />
										</ListItemAvatar>
										<ListItemText primary={renderMessageText(value)} />
									</ListItemButton>
								</Fragment>
							))}
					</Box>
				)}

				{/* Recent Section */}
				{completedMessList?.length > 0 && searchString === '' && (
					<Box>
						{[...completedMessList]
							?.sort((a, b) => b?.createdDate?.localDateTimeLong - a?.createdDate?.localDateTimeLong)
							?.filter((item) => !isDateOlder(item?.createdDate?.localDateTimeStringAMPM))
							?.map((value, index) => (
								<Fragment key={index}>
									{index === 0 && (
										<Box sx={{ px: 0, pt: 0, pb: '12px' }}>
											<Typography sx={{ color: '#1E293B', fontSize: '1.04rem', fontWeight: 500 }}>Recent</Typography>
										</Box>
									)}
									<ListItemButton key={index} sx={getItemSx(isUnreadMessage(value))} onClick={() => clickHandler(value)}>
										<ListItemAvatar>
											<UserAvatar3 userId={value?.userId} width={48} height={48} />
										</ListItemAvatar>
										<ListItemText primary={renderMessageText(value)} />
									</ListItemButton>
								</Fragment>
							))}

						{/* Old Message Section */}
						{[...completedMessList]
							?.sort((a, b) => b?.createdDate?.localDateTimeLong - a?.createdDate?.localDateTimeLong)
							.filter((item) => isDateOlder(item?.createdDate?.localDateTimeStringAMPM))
							.map((value, index) => (
								<Fragment key={index}>
									{index === 0 && (
										<Box sx={{ px: 0, pt: 0, pb: '12px', mt: 0.5, borderTop: '1px solid #E2E8F0' }}>
											<Typography sx={{ color: '#1E293B', fontSize: '1.04rem', fontWeight: 500 }}>Older</Typography>
										</Box>
									)}
									<ListItemButton key={index} sx={getItemSx(isUnreadMessage(value))} onClick={() => clickHandler(value)}>
										<ListItemAvatar>
											<UserAvatar3 userId={value?.userId} width={48} height={48} />
										</ListItemAvatar>
										<ListItemText primary={renderMessageText(value)} />
									</ListItemButton>
								</Fragment>
							))}
					</Box>
				)}
			</InfiniteScroll>
		</List>
	);
}

export default MessageContent;

function MessageSearchContent({ search, clickHandler, isUnreadMessage }) {
	return (
		<List component="nav" sx={LIST_SX}>
			<Box sx={{ px: 0, pt: 0, pb: '12px' }}>
				<Typography sx={{ color: '#1E293B', fontSize: '1.04rem', fontWeight: 500 }}>Search</Typography>
			</Box>
			{search.map((value, index) => (
				<ListItemButton key={index} sx={getItemSx(isUnreadMessage(value))} onClick={() => clickHandler(value)}>
					<ListItemAvatar>
						<UserAvatar3 userId={value?.userId} width={48} height={48} />
					</ListItemAvatar>
					<ListItemText primary={renderMessageText(value)} />
				</ListItemButton>
			))}
		</List>
	);
}
